import { Button, ImagePreview } from "@attmos/flowstack-ui";
import { useMutation } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { toast } from "sonner";

import { apiDownload, ApiError } from "@/api/client";
import type { TorsionScanGeometryResponse } from "@/api/scan-geometry";
import { useWavefrontPlot } from "@/api/wavefront-plot";

/** What the preview says for any failure: a 404, a failed fetch, a non-PNG. */
export const WAVEFRONT_PLOT_UNAVAILABLE = "Plot unavailable";

/**
 * FFPOpt's wavefront scan plot for one torsion's scan, under the structure it
 * belongs beside — on the Analysis tab and in the run workspace alike.
 *
 * The wiring only: the picture is flowstack-ui's `ImagePreview`, never a local
 * `<img>`, and the bytes come from {@link useWavefrontPlot}. Renders nothing
 * when the scan names no plot (`wavefront_plot: null`). Whether a surface
 * shows it at all is that surface's call (the Analysis tab's D7 rule), made by
 * not mounting this.
 */
export function WavefrontPlotPreview({
  scan,
}: {
  scan: TorsionScanGeometryResponse | undefined;
}): JSX.Element | null {
  const plot = scan?.wavefront_plot ?? null;
  const { src, loading, error } = useWavefrontPlot(plot);
  const download = useMutation({
    mutationFn: async () => {
      if (plot) await apiDownload(plot.download_url, plot.payload_name);
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof ApiError && err.message ? err.message : "Download failed. Please try again."
      );
    },
  });

  if (!scan || !plot) return null;
  return (
    <div data-testid="wavefront-plot">
      <ImagePreview
        src={src}
        alt={`Wavefront scan plot, torsion ${scan.torsion_id}`}
        loading={loading}
        error={error ? WAVEFRONT_PLOT_UNAVAILABLE : null}
        caption="Wavefront scan plot"
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            <Download className="mr-1 h-4 w-4" aria-hidden />
            Download PNG
          </Button>
        }
      />
    </div>
  );
}
